import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomTextInput from '../../../components/CustomTextInput';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useClaimBusinessProfileHandler, useRequestClaimOtpHandler } from '../dataHandlers/claimBusinessProfileHandler';
import appConfig from '../../../config/appConfig';

const WIKI_GUIDE_URL = appConfig.productWikiUrl.trim();
const EMARATAX_PORTAL_URL = 'https://eservices.tax.gov.ae/#/Logon';
const FORM_ID = 'setup-business-profile-form';
const OTP_RESEND_SECONDS = 120;

const EXTERNAL_LINK_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: '0.875rem',
  fontWeight: 500,
};

const createSetupSchema = (step, t) =>
  Yup.object().shape({
    email: Yup.string().email(t.invalid_email).required(t.email_required),
    tin: Yup.string().required(t.tin_required).trim().length(10, t.tin_length).matches(/^1/, t.tin_starts_with_one),
    otp: Yup.string().when([], {
      is: () => step === 'otp',
      then: (schema) => schema.required(t.otp_required).matches(/^\d{6}$/, t.otp_length),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

const formatCountdown = (template, seconds) =>
  template
    .replace('{minutes}', String(Math.floor(seconds / 60)))
    .replace('{seconds}', String(seconds % 60).padStart(2, '0'));

const SetupBusinessProfileModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wasOpenRef = useRef(open);
  const [step, setStep] = useState('details');
  const [timer, setTimer] = useState(0);
  const { businessProfiles, language } = useTranslatedMessages();
  const m = businessProfiles?.setup_modal ?? {};
  const v = m.validations ?? {};
  const setupSnackbar = businessProfiles?.snackbars ?? {};
  const brandName = appConfig.brandName;
  const isOtpStep = step === 'otp';

  const schema = createSetupSchema(step, v);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    trigger,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      tin: '',
      otp: '',
    },
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  const resetModal = useCallback(() => {
    reset({ email: '', tin: '', otp: '' });
    setStep('details');
    setTimer(0);
  }, [reset]);

  const handleClose = useCallback(() => {
    resetModal();
    onClose();
  }, [resetModal, onClose]);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      resetModal();
    }
    wasOpenRef.current = open;
  }, [open, resetModal]);

  useEffect(() => {
    if (timer <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      setTimer((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timer > 0]);

  const { onSubmit: requestOtp, isPending: isRequestingOtp } = useRequestClaimOtpHandler({
    onSuccess: () => {
      setStep('otp');
      setTimer(OTP_RESEND_SECONDS);
    },
  });

  const sendOtpRequest = useCallback(() => {
    requestOtp({
      email: getValues('email'),
      tin: getValues('tin'),
    });
  }, [getValues, requestOtp]);

  const handleGenerateOtp = useCallback(async () => {
    if (!(await trigger(['email', 'tin']))) {
      return;
    }
    sendOtpRequest();
  }, [trigger, sendOtpRequest]);

  const { onSubmit: claimProfile, isPending: isClaiming } = useClaimBusinessProfileHandler({
    onSuccess: (businessProfile) => {
      const profileId = businessProfile?.profile_id;
      handleClose();
      if (!profileId) {
        dispatch(
          showSnackbar({
            message: setupSnackbar.claim_error ?? 'Failed to claim business profile. Please try again.',
            severity: 'error',
          }),
        );
        return;
      }
      navigate(`/business-profiles/${profileId}/edit`);
    },
  });

  const handleClaimTin = useCallback(
    (data) => {
      claimProfile({
        email: data.email,
        tin: data.tin,
        otp: data.otp,
      });
    },
    [claimProfile],
  );

  const email = watch('email');

  const infoMessage = (
    m.info_message ??
    'Before you can initiate the business profile setup on {brandName}, you will need to link {brandName} as your ASP from the EmaraTax portal.'
  )
    .split('{brandName}')
    .join(brandName);

  const resendLabel = formatCountdown(m.resend_otp_in ?? 'Resend OTP in {minutes}:{seconds}', timer);

  const otpSentHint = (m.otp_sent_hint ?? 'OTP sent to {email}.').replace('{email}', email || '');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: 550,
          maxWidth: '90vw',
          overflow: 'visible',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" component="span" fontWeight={700} sx={{ pr: 2 }}>
          {m.title ?? 'Set up Business Profile'}
        </Typography>
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2, overflow: 'visible' }}>
        <Box
          sx={{
            backgroundColor: '#EEF2FF',
            border: '1px solid #C7D2FE',
            borderRadius: 2,
            p: 2,
            mb: 2.5,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ color: 'primary.main', fontSize: 22, mt: 0.25 }} />
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.primary">
                {infoMessage}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {WIKI_GUIDE_URL ? (
                  <Link
                    href={WIKI_GUIDE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={EXTERNAL_LINK_SX}
                  >
                    <MenuBookOutlinedIcon sx={{ fontSize: 18 }} />
                    {m.wiki_guide_link ?? 'View Step-by-Step Wiki Guide'}
                  </Link>
                ) : null}
                <Link
                  href={EMARATAX_PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={EXTERNAL_LINK_SX}
                >
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                  {m.emaratax_portal_link ?? 'Go to EmaraTax Portal'}
                </Link>
              </Stack>
            </Stack>
          </Stack>
        </Box>
        <Stack spacing={2.5} component="form" id={FORM_ID} onSubmit={handleSubmit(handleClaimTin)} sx={{ mt: 0.5 }}>
          <CustomTextInput
            data-testid="setup-business-profile-email"
            name="email"
            control={control}
            errors={errors}
            disabled={isOtpStep}
            label={m.email_label ?? 'EmaraTax Email Address'}
            placeholder={m.email_placeholder ?? 'name@company.ae'}
          />
          <CustomTextInput
            data-testid="setup-business-profile-tin"
            name="tin"
            control={control}
            errors={errors}
            disabled={isOtpStep}
            label={m.tin_label ?? 'Tax Identification Number (TIN)'}
            placeholder={m.tin_placeholder ?? '10-digit TIN'}
            inputProps={{ maxLength: 10 }}
          />

          {isOtpStep && (
            <>
              <CustomTextInput
                data-testid="setup-business-profile-otp"
                name="otp"
                control={control}
                errors={errors}
                label={m.otp_label ?? 'Enter Verification OTP'}
                placeholder={m.otp_placeholder ?? '6-digit OTP'}
                inputProps={{ maxLength: 6 }}
              />
              <Stack spacing={0.5} direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {otpSentHint}
                </Typography>
                {timer > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {resendLabel}
                  </Typography>
                ) : (
                  <Button
                    data-testid="setup-business-profile-resend-otp"
                    onClick={sendOtpRequest}
                    variant="text"
                    color="primary"
                    disabled={isRequestingOtp}
                    sx={{ alignSelf: 'flex-end', p: 0, minWidth: 'auto' }}
                  >
                    {m.resend_otp ?? 'Resend OTP'}
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button data-testid="setup-business-profile-cancel" variant="outlined" onClick={handleClose}>
          {m.cancel ?? 'Cancel'}
        </Button>
        {isOtpStep ? (
          <Button
            data-testid="setup-business-profile-claim-tin"
            type="submit"
            form={FORM_ID}
            variant="contained"
            color="primary"
            disabled={isClaiming}
          >
            {isClaiming ? (m.claiming_tin ?? 'Claiming TIN...') : (m.claim_tin ?? 'Claim TIN')}
          </Button>
        ) : (
          <Button
            data-testid="setup-business-profile-generate-otp"
            variant="contained"
            color="primary"
            onClick={handleGenerateOtp}
            disabled={isRequestingOtp}
          >
            {isRequestingOtp ? (m.generating_otp ?? 'Generating OTP...') : (m.generate_otp ?? 'Generate OTP')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SetupBusinessProfileModal;
